import {Controller, useFormContext} from 'react-hook-form';
import * as React from 'react';
import {useCallback} from 'react';
import {FormControl, FormHelperText, InputLabel, MenuItem} from '@mui/material';
import Select from '@mui/material/Select';
import {SelectProps} from '@mui/material/Select/Select';
import {nunitoBase} from "../../technical/style/theme";

const EMPTY_VALUE = '__empty__';

export function MuiRhfSelectField<LIST_TYPE>(props: {
  name: string,
  label?: string,
  list: LIST_TYPE[],
  getListLabel?: (entry: LIST_TYPE) => string,
  getListKey?: (entry: LIST_TYPE) => string | number,
  noSelectionText?: string,
  allowEmpty?: boolean,
  helperText?: string,
} & Partial<Omit<SelectProps, 'onChange' | 'onBlur' | 'value' | 'name' | 'error'>>) {
  const {
    name,
    label,
    list,
    getListLabel,
    getListKey,
    noSelectionText,
    allowEmpty,
    required,
    helperText,
    ...selectAndFormControlProps
  } = props;
  const { variant, ...rest } = selectAndFormControlProps;
  const { control } = useFormContext<any>();


  const getListKeyOrDefault = useCallback((value: any) => {
    if (value === EMPTY_VALUE) {
      return value;
    }
    let result: string | number = '';
    if (value) {
      if (getListKey) {
        result = getListKey(value);
      } else {
        result = value.toString();
      }
    }
    if (!result) {
      result = '';
    }
    return result;
  }, [getListKey]);

  const getListLabelOrDefault = useCallback((value: LIST_TYPE) => {
    if (!value || list.length === 0) {
      return '';
    }
    if (!getListLabel) {
      return (value as any).toString();
    }
    return getListLabel(value);
  }, [getListLabel, list]);

  return (
    <Controller
      name={name}
      control={control}
      render={renderProps => {
        return (
          <FormControl error={!!renderProps.fieldState.error} fullWidth={true} variant={variant}>
            {label &&
              <InputLabel id={'select_' + name + '_label'}>{label + (props.required ? ' *' : '')}</InputLabel>}
            <Select
              id={'select_' + name}
              labelId={'select_' + name + '_label'}
              label={label}
              name={renderProps.field.name}
              onChange={event => {
                const e = event as {} as React.ChangeEvent<HTMLSelectElement>;
                if (e.target.value === EMPTY_VALUE) {
                  renderProps.field.onChange(undefined);
                } else {
                  let value = list.find((entry: LIST_TYPE) => getListKeyOrDefault(entry) === e.target.value);
                  if (value) {
                    renderProps.field.onChange(value);
                  }
                }
              }
              }
              onBlur={renderProps.field.onBlur}
              value={getListKeyOrDefault(renderProps.field.value)}
              error={renderProps.fieldState.invalid}
              {...rest}
            >
              <MenuItem value={EMPTY_VALUE} disabled={!allowEmpty} key={EMPTY_VALUE}>{noSelectionText || '---'}</MenuItem>
              {list.map((item) =>
                <MenuItem
                  id={'option_' + getListKeyOrDefault(item)}
                  value={getListKeyOrDefault(item)}
                  key={getListKeyOrDefault(item)}
                  sx={{ ...nunitoBase, }}
                >{getListLabelOrDefault(item)}
                </MenuItem>)}
            </Select>
            <FormHelperText>{renderProps.fieldState.error?.message || helperText}</FormHelperText>
          </FormControl>
        );
      }}
    />
  );
}
